import React, { useEffect, useState, useCallback } from 'react';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import FilterBar from '../../components/common/FilterBar';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PersonSelect from '../../components/PersonSelect';
import { billingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const fmtErr = (d, fb) => { if (!d) return fb; if (typeof d === 'string') return d; if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0]}: ${e.msg}` : JSON.stringify(e)).join('; '); return d.msg || d.message || JSON.stringify(d); };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 5 };

const PAY_STATUSES = ['PENDING', 'PAID', 'INSURANCE_PROCESSING', 'OVERDUE', 'REFUNDED'];
const PAY_METHODS  = ['CASH', 'MPESA', 'BANK_TRANSFER', 'INSURANCE', 'CARD'];

const FILTER_DEF = [
  { key: 'payment_status', label: 'Status',    type: 'select', options: PAY_STATUSES },
  { key: 'date_from',      label: 'From Date', type: 'date' },
  { key: 'date_to',        label: 'To Date',   type: 'date' },
];
const BLANK_F = { payment_status: '', date_from: '', date_to: '' };

const BLANK_CF = {
  patient_id: '', visit_id: '', service_cost: '', tax_amount: '0',
  discount_amount: '0', insurance_covered: '0', due_in_days: '14', description: '',
};

export default function Billing() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING'].includes(r));
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const PER = 50;
  const [filters, setFilters] = useState(BLANK_F);

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [cf, setCf] = useState(BLANK_CF);
  const [unbilled, setUnbilled] = useState([]);
  // rateMap: { SERVICE_TYPE: base_rate_kes } built from the list returned by /billing/rates
  const [rateMap, setRateMap] = useState({});
  const [defaultRate, setDefaultRate] = useState(2500);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [cErr, setCErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit
  const [editRow, setEditRow] = useState(null);
  const [ef, setEf]           = useState({ payment_status: 'PENDING', payment_method: 'CASH', notes: '' });
  const [eErr, setEErr]       = useState('');
  const [eSaving, setESaving] = useState(false);

  // Void
  const [voidRow, setVoidRow]   = useState(null);
  const [voidLoading, setVoid]  = useState(false);

  const buildParams = useCallback(() => {
    const p = { page, per_page: PER };
    if (filters.payment_status) p.payment_status = filters.payment_status;
    if (filters.date_from)      p.date_from       = filters.date_from;
    if (filters.date_to)        p.date_to         = filters.date_to;
    return p;
  }, [filters, page]);

  const load = useCallback(() => {
    setLoading(true);
    billingAPI.list(buildParams())
      .then(r => { setRows(r.data || []); setTotal(r.meta?.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  const loadCreateData = useCallback(() => {
    billingAPI.unbilledVisits()
      .then(r => setUnbilled(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setUnbilled([]));
    // /billing/rates returns a LIST of rate card objects [{service_type, base_rate_kes, ...}]
    // Build a lookup map: { SERVICE_TYPE: base_rate_kes }
    billingAPI.rates()
      .then(r => {
        const cards = Array.isArray(r?.data) ? r.data : [];
        const map = {};
        let fallback = 2500;
        cards.forEach(c => {
          if (c.service_type && c.base_rate_kes) {
            // If multiple cards per service_type, prefer the one with no specialization (general)
            if (!map[c.service_type] || !c.specialization) {
              map[c.service_type] = parseFloat(c.base_rate_kes);
            }
          }
          if (c.service_type === 'GENERAL_NURSING') fallback = parseFloat(c.base_rate_kes) || 2500;
        });
        setRateMap(map);
        setDefaultRate(fallback);
      })
      .catch(() => { setRateMap({}); setDefaultRate(2500); });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => {
    if (isPatientOnly) return;
    loadCreateData();
  }, [isPatientOnly, loadCreateData]);

  // Selecting an unbilled visit auto-fills patient + the rate-card amount.
  const pickVisit = (vid) => {
    if (!vid) {
      setCf(f => ({ ...f, visit_id: '', patient_id: '', service_cost: '', description: '' }));
      return;
    }
    const v = unbilled.find(x => x.visit_id === vid);
    if (!v) return;
    const serviceType = v.service_type || v.visit_type || '';
    const suggested = serviceType ? (rateMap[serviceType] ?? defaultRate) : defaultRate;
    const label = serviceType ? serviceType.replace(/_/g, ' ') : 'Service';
    setCf(f => ({
      ...f,
      visit_id:     vid,
      patient_id:   v.patient_id || f.patient_id,
      service_cost: f.service_cost || String(suggested),
      description:  f.description || `${label} visit`,
    }));
  };

  const num = (x) => parseFloat(x) || 0;
  const copay = Math.max(0, num(cf.service_cost) + num(cf.tax_amount) - num(cf.discount_amount) - num(cf.insurance_covered));

  const handleCreate = async e => {
    e.preventDefault();
    if (!cf.patient_id) { setCErr('Please select a patient.'); return; }
    if (!num(cf.service_cost)) { setCErr('Service cost must be greater than zero.'); return; }
    setSaving(true); setCErr('');
    try {
      const resp = await billingAPI.create({
        patient_id:        cf.patient_id,
        visit_id:          cf.visit_id || undefined,
        service_cost:      num(cf.service_cost),
        tax_amount:        num(cf.tax_amount),
        discount_amount:   num(cf.discount_amount),
        insurance_covered: num(cf.insurance_covered),
        due_in_days:       parseInt(cf.due_in_days, 10) || 14,
        description:       cf.description || undefined,
      });
      const d = resp?.data ?? resp;
      setCreatedInfo(d);
      setShowCreate(false);
      setCf(BLANK_CF);
      loadCreateData();
      load();
    } catch (err) { setCErr(fmtErr(err.response?.data?.detail, 'Failed to create invoice')); }
    finally { setSaving(false); }
  };

  const openEdit = row => {
    setEditRow(row);
    setEf({ payment_status: row.payment_status, payment_method: row.payment_method || 'CASH', notes: row.notes || '' });
    setEErr('');
  };
  const handleEdit = async e => {
    e.preventDefault(); setESaving(true); setEErr('');
    try {
      await billingAPI.update(editRow.billing_id, {
        payment_status: ef.payment_status,
        payment_method: ef.payment_method || undefined,
        notes:          ef.notes          || undefined,
      });
      setEditRow(null); load();
    } catch (err) { setEErr(fmtErr(err.response?.data?.detail, 'Failed to update')); }
    finally { setESaving(false); }
  };

  const handleVoid = async () => {
    setVoid(true);
    try { await billingAPI.void(voidRow.billing_id); setVoidRow(null); load(); }
    catch (err) { alert(fmtErr(err.response?.data?.detail, 'Void failed')); }
    finally { setVoid(false); }
  };

  const totalInvoiced  = rows.reduce((s, r) => s + Number(r.service_cost    || 0), 0);
  const totalCollected = rows.filter(r => r.payment_status === 'PAID').reduce((s, r) => s + Number(r.patient_copay || r.service_cost || 0), 0);
  const totalInsurance = rows.reduce((s, r) => s + Number(r.insurance_covered || 0), 0);
  const outstanding    = totalInvoiced - totalCollected;

  const columns = [
    { key: 'invoice_number',  label: 'Invoice #',  render: v => v || '—' },
    { key: 'patient_id',      label: 'Patient',    render: (_, r) => isPatientOnly ? (user?.name || 'You') : (r.patient_name || r.patient_id?.slice(0, 8) || '—') },
    { key: 'service_cost',    label: 'Amount',     render: v => `KES ${Number(v || 0).toLocaleString()}` },
    { key: 'patient_copay',   label: 'Copay',      render: v => v != null ? `KES ${Number(v).toLocaleString()}` : '—' },
    { key: 'insurance_covered',label:'Insurance',  render: v => `KES ${Number(v || 0).toLocaleString()}` },
    { key: 'payment_method',  label: 'Method',     render: v => v ? v.replace(/_/g, ' ') : '—' },
    { key: 'payment_status',  label: 'Status',     render: v => <Badge value={v} /> },
    { key: 'created_at',      label: 'Date',       render: v => v ? new Date(v).toLocaleDateString() : '—' },
    ...(isPatientOnly ? [] : [{
      key: '_actions', label: 'Actions', render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => openEdit(row)} style={btnSm('#94335a')} disabled={row.payment_status === 'REFUNDED'}>✏️</button>
          <button onClick={() => setVoidRow(row)} style={btnSm('#ef4444')} disabled={['REFUNDED', 'PAID'].includes(row.payment_status)}>✕ Void</button>
        </div>
      ),
    }]),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>{isPatientOnly ? 'My Invoices & Payments' : 'Billing & Payments'}</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>{isPatientOnly ? 'Your invoices and payment history.' : `${total} invoices · showing ${rows.length}`}</p>
        </div>
        {!isPatientOnly && <button onClick={() => { setShowCreate(true); setCErr(''); setCf(BLANK_CF); }} style={btnPrimary('#6366f1')}>+ New Invoice</button>}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total Invoiced',  value: `KES ${totalInvoiced.toLocaleString()}`,  color: '#94335a' },
          { label: 'Collected',       value: `KES ${totalCollected.toLocaleString()}`, color: '#10b981' },
          { label: 'Insurance',       value: `KES ${totalInsurance.toLocaleString()}`, color: '#8b5cf6' },
          { label: 'Outstanding',     value: `KES ${outstanding.toLocaleString()}`,    color: '#ef4444' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderLeft: `4px solid ${color}` }}>
            <div style={{ fontSize: 11, color: '#8b7681', fontWeight: 600, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#2b1b22' }}>{value}</div>
          </div>
        ))}
      </div>

      {createdInfo && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, fontSize: 13, background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✅ Invoice <strong>{createdInfo.invoice_number}</strong> created — patient owes KES {Number(createdInfo.patient_copay || 0).toLocaleString()}, due {createdInfo.due_date}. The patient has been notified.</span>
          <button onClick={() => setCreatedInfo(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#065f46', fontSize: 15 }}>✕</button>
        </div>
      )}

      <FilterBar filters={FILTER_DEF} values={filters} onChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))} onClear={() => setFilters(BLANK_F)} />

      {loading ? <div style={{ color: '#8b7681', padding: 20 }}>Loading…</div> : <Table columns={columns} data={rows} />}
      <Pagination page={page} total={total} per={PER} onPage={setPage} />

      {/* ── Create Modal ── */}
      {showCreate && (
        <Modal title="New Invoice" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {cErr && <ErrBox msg={cErr} />}
          <form onSubmit={handleCreate}>

            {/* Row 1: Visit picker + Patient */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Link to a completed visit (auto-fills patient &amp; rate)</label>
                <select style={inp} value={cf.visit_id} onChange={e => pickVisit(e.target.value)}>
                  <option value="">— Standalone invoice (no visit) —</option>
                  {unbilled.map(v => {
                    const svcLabel = (v.service_type || v.visit_type || 'Visit').replace(/_/g, ' ');
                    const dateLabel = v.scheduled_start ? new Date(v.scheduled_start).toLocaleDateString() : '';
                    const patLabel  = v.patient_name || '';
                    return (
                      <option key={v.visit_id} value={v.visit_id}>
                        {[patLabel, svcLabel, dateLabel].filter(Boolean).join(' · ')}
                      </option>
                    );
                  })}
                </select>
                {unbilled.length === 0 && (
                  <div style={{ fontSize: 11, color: '#a99aa0', marginTop: 4 }}>
                    No unbilled completed visits found.
                  </div>
                )}
              </div>
              <div>
                <label style={lbl}>Patient *</label>
                {cf.visit_id ? (
                  <div style={{ padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, background: '#f9fafb', color: '#6b7280' }}>
                    {unbilled.find(v => v.visit_id === cf.visit_id)?.patient_name || cf.patient_id?.slice(0, 8) || '—'}
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>(set by visit)</span>
                  </div>
                ) : (
                  <PersonSelect
                    role="patient"
                    value={cf.patient_id}
                    onChange={id => setCf(f => ({ ...f, patient_id: id }))}
                    required
                  />
                )}
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Line description</label>
              <input style={inp} value={cf.description}
                placeholder="e.g. Wound care visit, Initial consultation, Dressing supplies"
                onChange={e => setCf(f => ({ ...f, description: e.target.value }))} />
            </div>

            {/* Amounts row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
              <F label="Service cost (KES) *" k="service_cost"    type="number" form={cf} set={setCf} required />
              <F label="Tax (KES)"            k="tax_amount"      type="number" form={cf} set={setCf} />
              <F label="Discount (KES)"       k="discount_amount" type="number" form={cf} set={setCf} />
              <F label="Insurance covered"    k="insurance_covered" type="number" form={cf} set={setCf} />
              <F label="Due in (days)"        k="due_in_days"     type="number" form={cf} set={setCf} />
            </div>

            {/* Copay preview */}
            <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: '#eef2ff', border: '1px solid #c7d2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, color: '#3730a3' }}>
                Cost {num(cf.service_cost).toLocaleString()} + tax {num(cf.tax_amount).toLocaleString()} − discount {num(cf.discount_amount).toLocaleString()} − insurance {num(cf.insurance_covered).toLocaleString()}
              </span>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#312e81' }}>
                Patient owes: KES {copay.toLocaleString()}
              </span>
            </div>

            <FmtActions saving={saving} onCancel={() => setShowCreate(false)} label="Create Invoice" color="#6366f1" />
          </form>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editRow && (
        <Modal title={`Update Invoice ${editRow.invoice_number || ''}`} onClose={() => setEditRow(null)}>
          {eErr && <ErrBox msg={eErr} />}
          <form onSubmit={handleEdit}>
            <Grid2>
              <SelF label="Payment Status" k="payment_status" form={ef} set={setEf} opts={PAY_STATUSES} />
              <SelF label="Payment Method" k="payment_method" form={ef} set={setEf} opts={PAY_METHODS} />
            </Grid2>
            <div style={{ marginTop: 14 }}>
              <label style={lbl}>Notes</label>
              <textarea style={{ ...inp, minHeight: 60 }} value={ef.notes} onChange={e => setEf(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <FmtActions saving={eSaving} onCancel={() => setEditRow(null)} label="Save Changes" color="#10b981" />
          </form>
        </Modal>
      )}

      {/* ── Void Confirm ── */}
      {voidRow && (
        <ConfirmDialog
          message={`Void invoice ${voidRow.invoice_number || voidRow.billing_id?.slice(0, 8)}?`}
          detail="The invoice will be marked as REFUNDED and cannot be reversed."
          onConfirm={handleVoid}
          onCancel={() => setVoidRow(null)}
          loading={voidLoading}
          confirmLabel="Void Invoice"
        />
      )}
    </div>
  );
}

const btnPrimary = bg => ({ padding: '9px 18px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const btnSm = bg => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' });
const ErrBox = ({ msg }) => <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{msg}</div>;
const Grid2 = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>;
const F = ({ label, k, type = 'text', form, set, required }) => (
  <div>
    <label style={lbl}>{label}</label>
    <input style={inp} type={type} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))} required={required} min={type === 'number' ? '0' : undefined} />
  </div>
);
const SelF = ({ label, k, form, set, opts }) => (
  <div>
    <label style={lbl}>{label}</label>
    <select style={inp} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))}>
      <option value="">— Select —</option>
      {opts.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
    </select>
  </div>
);
const FmtActions = ({ saving, onCancel, label, color = '#94335a' }) => (
  <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
    <button type="submit" disabled={saving} style={{ padding: '9px 22px', background: color, color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : label}</button>
    <button type="button" onClick={onCancel} style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
  </div>
);
const Pagination = ({ page, total, per, onPage }) => {
  const pages = Math.ceil(total / per); if (pages <= 1) return null;
  return <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20 }}>
    <button onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pgBtn}>‹</button>
    <span style={{ padding: '6px 12px', fontSize: 13, color: '#475569' }}>Page {page}/{pages}</span>
    <button onClick={() => onPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={pgBtn}>›</button>
  </div>;
};
const pgBtn = { padding: '6px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, cursor: 'pointer' };
